import React, { FC, ReactNode, useMemo } from "react";
import { useImmer } from "use-immer";

import { ColorSuccess, IgChip, IgTooltip } from "@intelligems/ig-theme";
import {
  AudienceDto,
  ChannelType,
  DeviceType,
  ExperienceDto,
  ExperienceListDto,
  ExperiencePageTargetingFe,
  VisitorType,
} from "@intelligems/ig-types";
import { hexToRgbA } from "@intelligems/ig-utils";
import { Check, CopyAll } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Grid } from "@mui/material";

import { useGetShopDomain } from "../../../../api/shops/get-domain";
import { ExpressionOverview } from "../../../../components/targeting/components/audienceTargeting/CommonExpression/ExpressionOverview";
import {
  formatCustomQueriesChip,
  getExpressionsState,
  getInitialChannelsState,
  getInitialCountriesState,
  getInitialDeviceState,
  getInitialVisitorState,
} from "../../../../components/targeting/components/audienceTargeting/utils";
import theme from "../../../../theme";
import { ExperienceFe } from "../../../../types/schemas/experiences";
import { copyToClipboard } from "../../../../utils/copyToClipboard";
import { renderConditionText } from "../../../experienceTests/utils/validators";

interface PersonalizationOverviewTargetingProps {
  audienceState: AudienceDto | undefined | null;
  personalization: ExperienceDto | ExperienceFe;
  pageTargeting: ExperiencePageTargetingFe[] | undefined | null;
  onPageTargetingChipClick?: () => void;
}
export const PersonalizationOverviewTargeting = ({
  audienceState,
  personalization,
  pageTargeting,
  onPageTargetingChipClick,
}: PersonalizationOverviewTargetingProps) => {
  const domain = useGetShopDomain();
  const [copyClicked, setCopyClicked] = useImmer(false);

  const onLinkCopy = async () => {
    await copyToClipboard(
      `${domain.data ? `${domain.data}/` : ""}${
        personalization.linkBaseUrl || ""
      }?ig-campaign=${personalization.id?.slice(-12)}`
    );

    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };

  const getExpressionsList = (index: number) => {
    if (audienceState) {
      const device = getInitialDeviceState(audienceState, index);
      const visitor = getInitialVisitorState(audienceState, index);
      const channel = getInitialChannelsState(audienceState, index);
      const country = getInitialCountriesState(audienceState, index);

      return getExpressionsState(
        device as DeviceType,
        visitor as VisitorType,
        country as string[],
        channel as ChannelType[]
      );
    }
  };

  const targetingChipArray = useMemo(() => {
    const componentArray: ReactNode[] = [];

    if (pageTargeting && pageTargeting.length > 0) {
      componentArray.push(
        <IgTooltip title={"Modifications limited to certain pages"}>
          <IgChip
            onClick={() => {
              onPageTargetingChipClick && onPageTargetingChipClick();
            }}
            sx={{
              height: "30px",
              borderRadius: "17px",
              backgroundColor: theme.palette.success.main,
              color: "white",
              "&:hover": {
                cursor: onPageTargetingChipClick ? "pointer" : "default",
                backgroundColor: !onPageTargetingChipClick
                  ? theme.palette.success.main
                  : theme.palette.success.light,
              },
            }}
            key={"page"}
            label={"Page"}
          />
        </IgTooltip>
      );
    }
    if (personalization.requiresLink) {
      componentArray.push(
        <IgTooltip
          title={
            copyClicked
              ? "Link Copied"
              : personalization.id !== undefined
                ? "Shown only to visitors with a special link. Click to copy link"
                : "Shown only to visitors with a special link."
          }
        >
          <IgChip
            onClick={() => {
              void onLinkCopy();
            }}
            icon={
              copyClicked ? (
                <Check
                  sx={{
                    fontSize: "16px",
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <ContentCopyIcon
                  sx={{
                    fontSize: "16px",
                    color: "#909090",
                    "&:hover": {
                      color: theme.palette.secondary.main,
                    },
                  }}
                />
              )
            }
            sx={{
              height: "30px",
              borderRadius: "17px",
              backgroundColor: copyClicked
                ? hexToRgbA(theme.palette.success.main, 0.5)
                : hexToRgbA(theme.palette.warning.main, 0.5),
              flexDirection: "row-reverse",
              "& .MuiChip-icon": {
                marginRight: "5px !important",
                marginLeft: "-6px !important",
              },
            }}
            key={"link"}
            label={"Link"}
            clickable={Boolean(personalization.id)}
          />
        </IgTooltip>
      );
    } else if (
      !audienceState ||
      (audienceState.filters.length === 0 &&
        !audienceState.excludeCurrency.exclude)
    ) {
      componentArray.push(
        <IgTooltip title={"Shown to all visitors"}>
          <IgChip
            sx={{
              height: "30px",
              borderRadius: "17px",
              backgroundColor: hexToRgbA("#1941E1", 0.08),
            }}
            key={"all"}
            label={"All Visitors"}
          />
        </IgTooltip>
      );
    }
    if (audienceState && audienceState.filters.length > 0) {
      componentArray.push(
        <Grid container direction={"row"}>
          {audienceState.filters.map((queryParam, index) => (
            <Grid item key={index}>
              <IgTooltip
                title={"Shown only to: " + renderConditionText(queryParam)}
              >
                {queryParam.expressionType === "custom" ? (
                  <IgChip
                    size={"medium"}
                    sx={{
                      backgroundColor: "#616161",
                      color: "white",
                      height: "30px",
                      borderRadius: "17px",
                    }}
                    label={formatCustomQueriesChip(queryParam)}
                  />
                ) : (
                  <ExpressionOverview
                    chipSx={{ height: "30px", borderRadius: "17px" }}
                    expressionsList={getExpressionsList(index)!}
                    onResetClick={() => {}}
                    drawer={true}
                    hideText={true}
                  />
                )}
              </IgTooltip>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (audienceState?.excludeCurrency?.exclude) {
      componentArray.push(
        <Grid item>
          <IgTooltip
            title={`Shown only to visitors in: ${audienceState.excludeCurrency.currency
              ?.split(",")
              .join(", ")}`}
          >
            <IgChip
              size={"medium"}
              sx={{
                height: "30px",
                borderRadius: "17px",
              }}
              label={audienceState.excludeCurrency.currency
                ?.split(",")
                .join(", ")}
            />
          </IgTooltip>
        </Grid>
      );
    }
    return componentArray;
  }, [audienceState, personalization, pageTargeting, copyClicked]);

  return (
    <Grid item container direction={"row"} columnSpacing={1}>
      {targetingChipArray.map((chip, index) => (
        <Grid item key={index}>
          {chip}
        </Grid>
      ))}
    </Grid>
  );
};
